<template>
  <div class="outbox-page" :class="device.desktop ? 'pa-6' : 'pa-0'">
    <v-row>
      <v-col cols="12">
        <v-card class="outbox-card animated animate__bounceIn elevation-0">
          <div class="outbox-toolbar">
            <div class="outbox-toolbar__left">
              <div class="outbox-toolbar__icon">
                <v-icon>mdi-send-outline</v-icon>
              </div>
              <div>
                <div class="outbox-toolbar__title">Pesan Keluar</div>
                <div class="outbox-toolbar__sub">
                  {{ records.length || 0 }} pesan dalam antrian / terkirim
                </div>
              </div>
            </div>

            <div class="outbox-toolbar__actions">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                placeholder="Cari penerima / pesan..."
                single-line
                hide-details
                dense
                outlined
                color="#1f7a57"
                class="outbox-search"
                clearable
              ></v-text-field>

              <v-tooltip bottom color="#0d4a36">
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    class="outbox-icon-btn"
                    v-on="on"
                    v-show="page.actions.refresh"
                    :loading="refreshing"
                    @click="refreshRecords"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Muat ulang</span>
              </v-tooltip>

              <v-tooltip bottom color="#0d4a36">
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    class="outbox-icon-btn outbox-icon-btn--danger"
                    v-on="on"
                    v-show="page.actions.bulkdelete || page.actions.add"
                    @click="postBulkDeleteRecord"
                  >
                    <v-icon>mdi-delete-sweep-outline</v-icon>
                  </v-btn>
                </template>
                <span>Hapus semua</span>
              </v-tooltip>

              <v-btn
                class="outbox-btn-primary"
                v-show="page.actions.add"
                @click="openForm"
              >
                <v-icon left small>mdi-plus</v-icon>
                Kirim Pesan
              </v-btn>
            </div>
          </div>

          <!-- Desktop table -->
          <v-data-table
            v-show="device.desktop"
            :headers="headers"
            :items="records"
            item-key="id"
            :items-per-page="50"
            :page.sync="table.options.page"
            class="outbox-table"
            :loading="loading.table && !silentUpdating"
            loading-text="Memuat pesan keluar..."
            :search="search"
            hide-default-footer
            @page-count="table.options.pageCount = $event"
          >
            <v-progress-linear
              slot="progress"
              color="#1f7a57"
              height="2"
              indeterminate
            ></v-progress-linear>

            <template v-slot:item.tanggal="{ value }">
              <div class="outbox-time">
                <v-icon x-small class="mr-1" color="#1f7a57">mdi-clock-outline</v-icon>
                {{ value || '—' }}
              </div>
            </template>

            <template v-slot:item.sender_number="{ value }">
              <span class="outbox-phone">{{ value || '—' }}</span>
            </template>

            <template v-slot:item.recieve_number="{ value }">
              <div class="outbox-recipient">
                <div class="outbox-recipient__mark">
                  <v-icon x-small>mdi-account</v-icon>
                </div>
                <span>{{ value || '—' }}</span>
              </div>
            </template>

            <template v-slot:item.content="{ item }">
              <div class="outbox-content-cell">
                <img
                  v-if="item.type === 'image' && imageSrc(item)"
                  :src="imageSrc(item)"
                  class="outbox-thumb"
                  alt="gambar"
                  @error="onThumbError($event)"
                />
                <div
                  v-else-if="item.type === 'document'"
                  class="outbox-doc-mark"
                >
                  <v-icon small color="#1f7a57">mdi-file-document-outline</v-icon>
                </div>
                <div class="outbox-content" :title="item.content || item.media">
                  <span v-if="item.type === 'image'" class="outbox-type-tag">Gambar</span>
                  <span v-else-if="item.type === 'document'" class="outbox-type-tag">Dokumen</span>
                  {{
                    item.content ||
                      (item.type === 'image'
                        ? '(tanpa caption)'
                        : item.type === 'document'
                        ? item.media || '(dokumen)'
                        : '—')
                  }}
                </div>
              </div>
            </template>

            <template v-slot:item.status="{ item }">
              <span
                class="outbox-status"
                :class="statusClass(item.status, item)"
              >
                <v-progress-circular
                  v-if="isPendingStatus(item.status, item) || isScheduledStatus(item)"
                  indeterminate
                  size="14"
                  width="2"
                  color="#1f7a57"
                  class="mr-2"
                ></v-progress-circular>
                <template v-else>
                  <span class="outbox-status__dot"></span>
                </template>
                <span v-if="isScheduledStatus(item)">
                  Jadwal {{ countdownText(item) }}
                </span>
                <span v-else-if="isPendingStatus(item.status, item)">
                  Mengirim...
                </span>
                <span v-else>
                  {{ statusText(item.status) }}
                </span>
              </span>
            </template>

            <template v-slot:item.id="{ value }">
              <div class="outbox-row-actions">
                <v-tooltip bottom color="#0d4a36">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      class="outbox-action-edit"
                      v-on="on"
                      v-show="page.actions.edit"
                      @click="editRecord(value)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Ubah</span>
                </v-tooltip>
                <v-tooltip bottom color="#0d4a36">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      class="outbox-action-delete"
                      v-on="on"
                      v-show="page.actions.delete"
                      @click="postDeleteRecord(value)"
                    >
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Hapus</span>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="outbox-empty">
                <v-icon large color="#8fd6b5">mdi-send-outline</v-icon>
                <div class="outbox-empty__title">Belum ada pesan keluar</div>
                <div class="outbox-empty__sub">
                  Kirim pesan WhatsApp pertama Anda sekarang
                </div>
                <v-btn
                  class="outbox-btn-primary mt-4"
                  small
                  v-show="page.actions.add"
                  @click="openForm"
                >
                  <v-icon left small>mdi-plus</v-icon>
                  Kirim Pesan
                </v-btn>
              </div>
            </template>
          </v-data-table>

          <!-- Mobile cards -->
          <div class="outbox-mobile" v-show="device.mobile">
            <div
              v-for="item in filterItems"
              :key="item.id"
              class="outbox-mobile-card"
            >
              <div class="outbox-mobile-card__top">
                <div>
                  <div class="outbox-mobile-card__to">
                    Ke: {{ item.recieve_number }}
                  </div>
                  <div class="outbox-time mt-1">{{ item.tanggal || '—' }}</div>
                </div>
                <v-menu bottom left origin="center center" transition="scale-transition">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" class="outbox-menu-btn">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="outbox-menu">
                    <v-list-item
                      @click="editRecord(item.id || item.uuid)"
                      v-show="page.actions.edit"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="#1f7a57">mdi-pencil</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Ubah Data</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click="postDeleteRecord(item.id || item.uuid)"
                      v-show="page.actions.delete"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="#c62828">mdi-delete-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Hapus Data</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>

              <div class="outbox-mobile-card__content">
                <img
                  v-if="item.type === 'image' && imageSrc(item)"
                  :src="imageSrc(item)"
                  class="outbox-thumb outbox-thumb--mobile"
                  alt="gambar"
                  @error="onThumbError($event)"
                />
                <div
                  v-else-if="item.type === 'document'"
                  class="outbox-doc-mark outbox-doc-mark--mobile"
                >
                  <v-icon small color="#1f7a57">mdi-file-document-outline</v-icon>
                </div>
                <div>
                  <span v-if="item.type === 'image'" class="outbox-type-tag">Gambar</span>
                  <span v-else-if="item.type === 'document'" class="outbox-type-tag">Dokumen</span>
                  {{
                    item.content ||
                      (item.type === 'image'
                        ? '(tanpa caption)'
                        : item.type === 'document'
                        ? item.media || '(dokumen)'
                        : '—')
                  }}
                </div>
              </div>

              <div class="outbox-mobile-card__meta">
                <span class="outbox-phone">Dari: {{ item.sender_number }}</span>
                <span
                  class="outbox-status"
                  :class="statusClass(item.status, item)"
                >
                  <v-progress-circular
                    v-if="isPendingStatus(item.status, item) || isScheduledStatus(item)"
                    indeterminate
                    size="14"
                    width="2"
                    color="#1f7a57"
                    class="mr-2"
                  ></v-progress-circular>
                  <template v-else>
                    <span class="outbox-status__dot"></span>
                  </template>
                  <span v-if="isScheduledStatus(item)">
                    Jadwal {{ countdownText(item) }}
                  </span>
                  <span v-else-if="isPendingStatus(item.status, item)">
                    Mengirim...
                  </span>
                  <span v-else>
                    {{ statusText(item.status) }}
                  </span>
                </span>
              </div>
            </div>

            <div v-if="!filterItems.length" class="outbox-empty pa-8">
              <v-icon large color="#8fd6b5">mdi-send-outline</v-icon>
              <div class="outbox-empty__title">Belum ada pesan keluar</div>
              <div class="outbox-empty__sub">
                Kirim pesan WhatsApp pertama Anda sekarang
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      transition="dialog-bottom-transition"
      v-model="form.add"
      :max-width="device.desktop ? '560px' : '100%'"
      persistent
      :fullscreen="device.mobile"
      content-class="outbox-dialog"
    >
      <v-card class="outbox-dialog-card">
        <div class="outbox-dialog-header">
          <div class="outbox-dialog-header__copy">
            <v-icon class="mr-2">mdi-message-plus-outline</v-icon>
            {{ form.edit ? 'Ubah Pesan Keluar' : 'Kirim Pesan Baru' }}
          </div>
          <v-btn icon dark small @click="closeForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="outbox-dialog-body">
          <div class="outbox-field-label">Nomor Pengirim</div>
          <v-select
            label=""
            outlined
            dense
            hide-details="auto"
            color="#1f7a57"
            item-color="#1f7a57"
            v-model="record.sender_number"
            :items="senders"
            class="outbox-field mb-4"
            prepend-inner-icon="mdi-whatsapp"
            placeholder="Pilih nomor pengirim"
          ></v-select>

          <div class="outbox-field-label">Nomor Penerima</div>
          <v-select
            label=""
            outlined
            dense
            hide-details="auto"
            color="#1f7a57"
            item-color="#1f7a57"
            v-model="record.recieve_number"
            :items="recieves"
            class="outbox-field mb-4"
            prepend-inner-icon="mdi-account-outline"
            placeholder="Pilih kontak penerima"
          ></v-select>

          <v-row dense class="mb-2">
            <v-col cols="12" sm="6">
              <div class="outbox-field-label">Tipe Pesan</div>
              <v-select
                outlined
                dense
                hide-details="auto"
                color="#1f7a57"
                item-color="#1f7a57"
                v-model="record.type"
                :items="messageTypes"
                class="outbox-field"
                prepend-inner-icon="mdi-file-document-outline"
                @change="onTypeChange"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="outbox-field-label">Waktu Pengiriman</div>
              <v-select
                outlined
                dense
                hide-details="auto"
                color="#1f7a57"
                item-color="#1f7a57"
                v-model="record.process"
                :items="processes"
                class="outbox-field"
                prepend-inner-icon="mdi-timer-outline"
              ></v-select>
            </v-col>
          </v-row>

          <template v-if="record.type === 'image'">
            <div class="outbox-field-label">Gambar</div>
            <div class="outbox-image-box mb-4">
              <div
                v-if="record.media_url || mediaPreview"
                class="outbox-image-preview"
              >
                <img :src="mediaPreview || record.media_url" alt="preview" />
                <v-btn
                  icon
                  small
                  class="outbox-image-remove"
                  @click="clearMedia"
                >
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
              <div v-else class="outbox-image-empty">
                <v-icon large color="#1f7a57">mdi-image-outline</v-icon>
                <div>Belum ada gambar dipilih</div>
              </div>
              <v-btn
                outlined
                class="outbox-btn-upload mt-3"
                :loading="uploadingMedia"
                @click="uploadImage"
              >
                <v-icon left small>mdi-upload</v-icon>
                {{ record.media ? 'Ganti Gambar' : 'Unggah Gambar' }}
              </v-btn>
              <div class="outbox-schedule-hint">
                Format JPG/PNG/WEBP, maks. 5MB. Caption opsional di bawah.
              </div>
            </div>
          </template>

          <template v-if="record.type === 'document'">
            <div class="outbox-field-label">Dokumen / File</div>
            <div class="outbox-image-box mb-4">
              <div v-if="record.media" class="outbox-doc-selected">
                <v-icon color="#1f7a57" class="mr-2">mdi-file-document-outline</v-icon>
                <div class="outbox-doc-selected__name">{{ record.media }}</div>
                <v-btn icon small class="outbox-image-remove" @click="clearMedia">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
              <div v-else class="outbox-image-empty">
                <v-icon large color="#1f7a57">mdi-file-upload-outline</v-icon>
                <div>Belum ada dokumen dipilih</div>
              </div>
              <v-btn
                outlined
                class="outbox-btn-upload mt-3"
                :loading="uploadingMedia"
                @click="uploadDocument"
              >
                <v-icon left small>mdi-upload</v-icon>
                {{ record.media ? 'Ganti Dokumen' : 'Unggah Dokumen' }}
              </v-btn>
              <div class="outbox-schedule-hint">
                PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, ZIP — maks. 15MB.
              </div>
            </div>
          </template>

          <div class="outbox-field-label">
            {{
              record.type === 'image' || record.type === 'document'
                ? 'Caption (opsional)'
                : 'Pesan'
            }}
          </div>
          <v-textarea
            outlined
            color="#1f7a57"
            hide-details="auto"
            :placeholder="
              record.type === 'image'
                ? 'Tulis caption gambar (opsional)...'
                : record.type === 'document'
                ? 'Tulis caption dokumen (opsional)...'
                : 'Tulis pesan WhatsApp Anda...'
            "
            v-model="record.content"
            rows="4"
            auto-grow
            dense
            class="outbox-field mb-4"
            prepend-inner-icon="mdi-message-text-outline"
          ></v-textarea>

          <v-row dense>
            <v-col cols="12" v-if="record.process === 'schedule'">
              <div class="outbox-field-label">Jadwal Kirim</div>
              <v-text-field
                outlined
                dense
                hide-details="auto"
                color="#1f7a57"
                type="datetime-local"
                v-model="record.scheduled_at"
                class="outbox-field"
                prepend-inner-icon="mdi-calendar-clock"
              ></v-text-field>
              <div class="outbox-schedule-hint">
                Pesan akan dikirim otomatis pada waktu yang dipilih
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider class="outbox-divider"></v-divider>
        <v-card-actions class="outbox-dialog-actions">
          <v-btn text class="outbox-btn-ghost" @click="closeForm">Batal</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            class="outbox-btn-primary"
            v-show="!form.edit"
            @click="postAddNewRecord"
          >
            <v-icon left small>mdi-send</v-icon>
            Kirim
          </v-btn>
          <v-btn
            class="outbox-btn-primary"
            v-show="form.edit"
            @click="postUpdateRecord"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Simpan
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import "animate.css";

export default {
  name: "PesanOutbox",
  data: () => ({
    headers: [
      {
        text: "Waktu",
        align: "start",
        sortable: true,
        value: "tanggal",
        width: 170,
      },
      {
        text: "Pengirim",
        align: "start",
        sortable: false,
        value: "sender_number",
        width: 140,
      },
      {
        text: "Penerima",
        align: "start",
        sortable: false,
        value: "recieve_number",
        width: 150,
      },
      { text: "Pesan", value: "content", sortable: false },
      {
        text: "Status",
        align: "center",
        sortable: false,
        value: "status",
        width: 140,
      },
      {
        text: "Aksi",
        value: "id",
        width: 110,
        sortable: false,
        align: "center",
      },
    ],
    search: null,
    senders: [],
    recieves: [],
    processes: [
      { text: "Langsung", value: "now" },
      { text: "Terjadwal", value: "schedule" },
    ],
    statusPoller: null,
    countdownTick: Date.now(),
    countdownTimer: null,
    silentUpdating: false,
    refreshing: false,
    mediaPreview: null,
    uploadingMedia: false,
  }),
  computed: {
    ...mapState([
      "page",
      "theme",
      "http",
      "device",
      "record",
      "records",
      "loading",
      "event",
      "snackbar",
      "form",
      "table",
      "user",
    ]),
    messageTypes() {
      const p = this.user && this.user.paket;
      if (!p || p.unlimited) {
        return [
          { text: "Text", value: "text" },
          { text: "Gambar", value: "image" },
          { text: "Dokumen", value: "document" },
        ];
      }
      const items = [];
      if (p.allow_text) items.push({ text: "Text", value: "text" });
      if (p.allow_image) items.push({ text: "Gambar", value: "image" });
      if (p.allow_document) items.push({ text: "Dokumen", value: "document" });
      return items;
    },
    filterItems() {
      if (!this.search) return this.records || [];
      const q = this.search.toLowerCase();
      return (this.records || []).filter((item) => {
        const receiver = (item.recieve_number || "").toLowerCase();
        const sender = (item.sender_number || "").toLowerCase();
        const content = (item.content || "").toLowerCase();
        return (
          receiver.indexOf(q) > -1 ||
          sender.indexOf(q) > -1 ||
          content.indexOf(q) > -1
        );
      });
    },
    hasPendingMessages() {
      return (this.records || []).some(
        (item) =>
          this.isPendingStatus(item.status, item) || this.isScheduledStatus(item)
      );
    },
  },
  watch: {
    records() {
      this.syncStatusPolling();
      this.syncCountdownTimer();
    },
  },
  created() {
    this.setPage({
      crud: true,
      dataUrl: "api/pesan/outbox",
      pagination: false,
      title: "PESAN KELUAR",
      subtitle: "Berikut Daftar Seluruh Pesan Keluar Yang Tersedia",
      showtable: true,
      breadcrumbs: [
        {
          text: "Pesan",
          disabled: true,
          href: "",
        },
        {
          text: "Pesan Keluar",
          disabled: false,
          href: "",
        },
      ],
      actions: {
        refresh: true,
        add: true,
        edit: true,
        delete: true,
        bulkdelete: true,
        print: false,
        export: false,
        help: false,
      },
    });
    this.fetchRecords();
  },
  mounted() {
    this.fetchSenders();
    this.fetchRecives();
    this.syncCountdownTimer();
  },
  beforeDestroy() {
    this.stopStatusPolling();
    this.stopCountdownTimer();
  },
  methods: {
    ...mapActions([
      "setPage",
      "fetchRecords",
      "postAddNew",
      "postEdit",
      "postUpdate",
      "postConfirmDelete",
      "postConfirmBulkDelete",
      "assignFileBrowse",
      "setLoading",
      "setRecord",
      "setForm",
    ]),
    statusText(value) {
      if (!value) return "Menunggu";
      if (typeof value === "object") return value.text || "Menunggu";
      return String(value);
    },
    imageSrc(item) {
      if (!item) return null;
      if (item.media_url) return item.media_url;
      if (item.media) {
        const base = ((this.page && this.page.domainUrl) || "").replace(
          /\/$/,
          ""
        );
        return `${base}/uploads/images/messages/${item.media}`;
      }
      return null;
    },
    onThumbError(event) {
      if (event && event.target) {
        event.target.style.display = "none";
      }
    },
    isScheduledStatus(item) {
      if (!item || item.sended) return false;
      const statusText = this.statusText(item.status).toLowerCase();
      if (item.process === "schedule" || statusText.includes("terjadwal")) {
        if (!item.scheduled_at) return true;
        return new Date(item.scheduled_at).getTime() > Date.now();
      }
      return false;
    },
    countdownText(item) {
      // depend on tick for reactive updates
      void this.countdownTick;
      if (!item?.scheduled_at) return "...";
      const diff = new Date(item.scheduled_at).getTime() - Date.now();
      if (diff <= 0) return "segera";
      const totalSec = Math.floor(diff / 1000);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      if (h > 0) {
        return `${h}j ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}d`;
      }
      return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    },
    isPendingStatus(value, item) {
      if (this.isScheduledStatus(item)) return false;

      const text = (
        typeof value === "object" ? value?.text || value?.color || "" : value || ""
      )
        .toString()
        .toLowerCase();

      // Gagal / terkirim bukan "mengirim"
      if (
        text.includes("gagal") ||
        text.includes("fail") ||
        text.includes("error") ||
        text.includes("terkirim") ||
        text.includes("sent") ||
        text.includes("success")
      ) {
        return false;
      }

      if (item && item.sended === true) return false;

      if (item && item.sended === false) {
        // menunggu / proses saja
        if (
          text.includes("menunggu") ||
          text.includes("antrian") ||
          text.includes("proses") ||
          text.includes("process") ||
          text.includes("wait") ||
          text.includes("grey") ||
          text.includes("orange") ||
          !text
        ) {
          return true;
        }
        return false;
      }

      if (!text) return true;

      return (
        text.includes("wait") ||
        text.includes("antrian") ||
        text.includes("pending") ||
        text.includes("process") ||
        text.includes("proses") ||
        text.includes("mengirim") ||
        text.includes("queue") ||
        text.includes("grey") ||
        text.includes("orange")
      );
    },
    statusClass(value, item) {
      if (this.isScheduledStatus(item)) return "is-queue";

      const text = (
        typeof value === "object" ? value?.text || value?.color || "" : value || ""
      )
        .toString()
        .toLowerCase();

      if (
        text.includes("fail") ||
        text.includes("gagal") ||
        text.includes("error") ||
        text.includes("red")
      ) {
        return "is-failed";
      }
      if (
        item?.sended === true ||
        text.includes("sent") ||
        text.includes("terkirim") ||
        text.includes("success") ||
        text.includes("green")
      ) {
        return "is-sent";
      }
      if (this.isPendingStatus(value, item) || text.includes("terjadwal")) {
        return "is-queue";
      }
      return "is-idle";
    },
    syncStatusPolling() {
      if (this.hasPendingMessages) {
        this.startStatusPolling();
      } else {
        this.stopStatusPolling();
      }
    },
    async silentRefreshRecords() {
      if (!this.page?.dataUrl) return;
      this.silentUpdating = true;
      try {
        const { data } = await this.http.get(this.page.dataUrl);
        if (!Array.isArray(data)) return;

        const prev = this.records || [];
        const prevMap = new Map(prev.map((row) => [row.id, row]));

        const merged = data.map((item) => {
          const existing = prevMap.get(item.id);
          if (!existing) return item;

          Object.keys(item).forEach((key) => {
            const nextVal = item[key];
            const prevVal = existing[key];
            if (typeof nextVal === "object" && nextVal !== null) {
              if (JSON.stringify(prevVal) !== JSON.stringify(nextVal)) {
                this.$set(existing, key, nextVal);
              }
            } else if (prevVal !== nextVal) {
              this.$set(existing, key, nextVal);
            }
          });
          return existing;
        });

        const sameStructure =
          merged.length === prev.length &&
          merged.every((row, index) => prev[index] && prev[index].id === row.id);

        if (!sameStructure) {
          // update daftar tanpa empty-flash
          this.$store.commit("RECORDS_MUTATION", merged);
        }
      } catch (error) {
        // diamkan error polling agar UI tetap stabil
      } finally {
        this.silentUpdating = false;
      }
    },
    async refreshRecords() {
      this.refreshing = true;
      try {
        await this.silentRefreshRecords();
      } finally {
        this.refreshing = false;
      }
    },
    startStatusPolling() {
      if (this.statusPoller) return;
      this.statusPoller = setInterval(() => {
        if (!this.hasPendingMessages) {
          this.stopStatusPolling();
          return;
        }
        this.silentRefreshRecords();
      }, 2500);
    },
    stopStatusPolling() {
      if (this.statusPoller) {
        clearInterval(this.statusPoller);
        this.statusPoller = null;
      }
    },
    syncCountdownTimer() {
      const hasSchedule = (this.records || []).some((item) =>
        this.isScheduledStatus(item)
      );
      if (hasSchedule) {
        this.startCountdownTimer();
      } else {
        this.stopCountdownTimer();
      }
    },
    startCountdownTimer() {
      if (this.countdownTimer) return;
      this.countdownTimer = setInterval(() => {
        this.countdownTick = Date.now();
        // jika countdown habis, sinkron status tanpa flicker
        const due = (this.records || []).some((item) => {
          if (!this.isScheduledStatus(item) && item.process === "schedule" && !item.sended) {
            return true;
          }
          if (!item?.scheduled_at || item.sended) return false;
          return (
            item.process === "schedule" &&
            new Date(item.scheduled_at).getTime() <= Date.now()
          );
        });
        if (due) {
          this.silentRefreshRecords();
        }
        this.syncCountdownTimer();
      }, 1000);
    },
    stopCountdownTimer() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    toLocalInputValue(date) {
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    openForm: function () {
      const soon = new Date(Date.now() + 10 * 60 * 1000);
      this.mediaPreview = null;
      this.uploadingMedia = false;
      this.setForm({
        add: true,
        edit: false,
      });
      this.setRecord({
        type: (this.messageTypes[0] && this.messageTypes[0].value) || "text",
        process: "now",
        content: "",
        media: null,
        media_url: null,
        scheduled_at: this.toLocalInputValue(soon),
      });
    },
    closeForm: function () {
      this.mediaPreview = null;
      this.uploadingMedia = false;
      this.setForm({
        add: false,
        edit: false,
      });
    },
    onTypeChange() {
      this.clearMedia();
    },
    clearMedia() {
      this.mediaPreview = null;
      this.$set(this.record, "media", null);
      this.$set(this.record, "media_url", null);
    },
    uploadImage() {
      this.assignFileBrowse({
        fileType: [".jpg", ".jpeg", ".png", ".webp"],
        maxSize: 5000000,
        query: {
          doctype: "messages",
        },
        callback: (response) => {
          this.uploadingMedia = false;
          if (!response || !response.name) {
            this.snackbar.color = "red";
            this.snackbar.text = "Gagal mengunggah gambar";
            this.snackbar.state = true;
            return;
          }
          this.$set(this.record, "media", response.name);
          this.$set(this.record, "media_url", response.path);
          this.mediaPreview = response.path;
        },
      });
      this.uploadingMedia = true;
      setTimeout(() => {
        this.uploadingMedia = false;
      }, 8000);
    },
    uploadDocument() {
      this.assignFileBrowse({
        fileType: [
          ".pdf",
          ".doc",
          ".docx",
          ".xls",
          ".xlsx",
          ".ppt",
          ".pptx",
          ".txt",
          ".csv",
          ".zip",
          ".rar",
          ".7z",
        ],
        maxSize: 15000000,
        query: {
          doctype: "documents",
        },
        callback: (response) => {
          this.uploadingMedia = false;
          if (!response || !response.name) {
            this.snackbar.color = "red";
            this.snackbar.text = "Gagal mengunggah dokumen";
            this.snackbar.state = true;
            return;
          }
          this.$set(this.record, "media", response.name);
          this.$set(this.record, "media_url", response.path);
        },
      });
      this.uploadingMedia = true;
      setTimeout(() => {
        this.uploadingMedia = false;
      }, 12000);
    },
    postAddNewRecord: function () {
      if (!this.record.sender_number || !this.record.recieve_number) {
        this.snackbar.color = "orange";
        this.snackbar.text = "Pilih nomor pengirim dan penerima";
        this.snackbar.state = true;
        return;
      }

      if (this.record.type === "image" && !this.record.media) {
        this.snackbar.color = "orange";
        this.snackbar.text = "Unggah gambar terlebih dahulu";
        this.snackbar.state = true;
        return;
      }

      if (this.record.type === "document" && !this.record.media) {
        this.snackbar.color = "orange";
        this.snackbar.text = "Unggah dokumen terlebih dahulu";
        this.snackbar.state = true;
        return;
      }

      if (
        this.record.type === "text" &&
        !(this.record.content || "").trim()
      ) {
        this.snackbar.color = "orange";
        this.snackbar.text = "Isi pesan terlebih dahulu";
        this.snackbar.state = true;
        return;
      }

      if (this.record.process === "schedule" && !this.record.scheduled_at) {
        this.snackbar.color = "orange";
        this.snackbar.text = "Pilih waktu jadwal pengiriman";
        this.snackbar.state = true;
        return;
      }

      const payload = {
        ...this.record,
        content: this.record.content || "",
        media:
          this.record.type === "image" || this.record.type === "document"
            ? this.record.media
            : null,
        scheduled_at:
          this.record.process === "schedule"
            ? new Date(this.record.scheduled_at).toISOString()
            : null,
      };

      this.setRecord(payload);
      this.postAddNew(payload).then(() => {
        this.closeForm();
        this.silentRefreshRecords().then(() => {
          this.startStatusPolling();
          this.syncCountdownTimer();
        });
      });
    },
    editRecord: function (val) {
      this.postEdit(val);
      this.setForm({
        add: true,
        edit: true,
      });
    },
    postUpdateRecord: function () {
      this.postUpdate(this.record).then(() => {
        this.closeForm();
        this.silentRefreshRecords();
      });
    },
    postDeleteRecord: function (val) {
      this.postConfirmDelete(val);
    },
    postBulkDeleteRecord: function () {
      this.postConfirmBulkDelete();
    },
    postDownload(val) {
      window.open(val, "__blank");
    },
    fetchSenders: async function () {
      try {
        let { data } = await this.http.get("api/combo/pengaturan");
        this.senders = data;
      } catch (error) {}
    },
    fetchRecives: async function () {
      try {
        let { data } = await this.http.get("api/combo/contact");
        this.recieves = data;
      } catch (error) {}
    },
  },
};
</script>

<style scoped>
.outbox-page {
  --gw-ink: #10241c;
  --gw-forest: #0d4a36;
  --gw-leaf: #1f7a57;
  --gw-mint: #8fd6b5;
  --gw-fog: #eef6f1;
  --gw-paper: #f8fbf9;
  --gw-line: #c9ddd3;
  --gw-muted: #5d7268;
}

.outbox-card {
  border-radius: 14px !important;
  overflow: hidden;
  border: 1px solid var(--gw-line);
  background: #fff !important;
}

.outbox-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1rem 1.15rem;
  background:
    radial-gradient(120% 80% at 0% 0%, #1a6b4d 0%, transparent 55%),
    linear-gradient(175deg, #0b3d2e 0%, #0f5c45 45%, #0a3327 100%);
  color: #f4fbf7;
}

.outbox-toolbar__left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.outbox-toolbar__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, #b7ebcf, #8fd6b5);
}

.outbox-toolbar__icon .v-icon {
  color: #0b3d2e !important;
}

.outbox-toolbar__title {
  font-family: "Outfit", "Montserrat", sans-serif;
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.2;
}

.outbox-toolbar__sub {
  margin-top: 0.15rem;
  font-size: 0.75rem;
  color: rgba(244, 251, 247, 0.65);
}

.outbox-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.outbox-search {
  max-width: 280px;
  min-width: 180px;
}

.outbox-search >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 10px !important;
  min-height: 38px !important;
}

.outbox-search >>> fieldset {
  border-color: rgba(143, 214, 181, 0.35) !important;
}

.outbox-search >>> input,
.outbox-search >>> .v-icon {
  color: #f4fbf7 !important;
}

.outbox-search >>> input::placeholder {
  color: rgba(244, 251, 247, 0.55) !important;
}

.outbox-icon-btn {
  color: #f4fbf7 !important;
}

.outbox-icon-btn:hover {
  background: rgba(143, 214, 181, 0.18) !important;
}

.outbox-icon-btn--danger:hover {
  background: rgba(198, 40, 40, 0.2) !important;
}

.outbox-btn-primary {
  background: linear-gradient(145deg, #b7ebcf, #8fd6b5) !important;
  color: #0b3d2e !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 700 !important;
  border-radius: 10px !important;
  box-shadow: none !important;
}

.outbox-btn-primary:hover {
  background: linear-gradient(145deg, #c8f0da, #9fdebf) !important;
}

.outbox-table {
  background: transparent !important;
}

.outbox-table >>> thead th {
  background: var(--gw-fog) !important;
  color: var(--gw-forest) !important;
  font-weight: 700 !important;
  font-size: 0.75rem !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 1px solid var(--gw-line) !important;
}

.outbox-table >>> tbody td {
  border-bottom: 1px solid var(--gw-line) !important;
  color: var(--gw-ink);
  height: 68px !important;
}

.outbox-table >>> tbody tr {
  transition: background-color 0.2s ease;
}

.outbox-table >>> tbody tr:hover {
  background: rgba(143, 214, 181, 0.08) !important;
}

.outbox-time {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  color: var(--gw-muted);
}

.outbox-phone {
  font-family: "Outfit", "Montserrat", sans-serif;
  font-weight: 600;
  color: var(--gw-forest);
  letter-spacing: 0.02em;
  font-size: 0.88rem;
}

.outbox-recipient {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--gw-ink);
}

.outbox-recipient__mark {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, #b7ebcf, #8fd6b5);
  flex-shrink: 0;
}

.outbox-recipient__mark .v-icon {
  color: #0b3d2e !important;
}

.outbox-content {
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--gw-muted);
  font-size: 0.9rem;
}

.outbox-content-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.outbox-thumb {
  width: 42px;
  height: 42px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--gw-line);
  flex-shrink: 0;
  background: #edf7f1;
}

.outbox-thumb--mobile {
  width: 64px;
  height: 64px;
  margin-bottom: 8px;
}

.outbox-doc-mark {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  border: 1px solid var(--gw-line);
  background: #edf7f1;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.outbox-doc-mark--mobile {
  width: 48px;
  height: 48px;
  margin-bottom: 8px;
}

.outbox-doc-selected {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.85rem 2.25rem 0.85rem 0.85rem;
  border-radius: 10px;
  background: var(--gw-fog);
  border: 1px solid var(--gw-line);
}

.outbox-doc-selected__name {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--gw-ink);
  word-break: break-all;
}

.outbox-type-tag {
  display: inline-block;
  margin-right: 6px;
  padding: 1px 6px;
  border-radius: 6px;
  background: rgba(31, 122, 87, 0.12);
  color: #1f7a57;
  font-size: 0.7rem;
  font-weight: 700;
  vertical-align: middle;
}

.outbox-image-box {
  border: 1px dashed rgba(31, 122, 87, 0.35);
  border-radius: 12px;
  padding: 14px;
  background: rgba(143, 214, 181, 0.08);
}

.outbox-image-preview {
  position: relative;
  display: inline-block;
}

.outbox-image-preview img {
  display: block;
  max-width: 100%;
  max-height: 220px;
  border-radius: 10px;
  object-fit: cover;
  border: 1px solid var(--gw-line);
}

.outbox-image-remove {
  position: absolute !important;
  top: 6px;
  right: 6px;
  background: rgba(255, 255, 255, 0.92) !important;
}

.outbox-image-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 110px;
  color: #5f7a6d;
  font-size: 0.85rem;
}

.outbox-btn-upload {
  color: #1f7a57 !important;
  border-color: #1f7a57 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}

.outbox-status {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid var(--gw-line);
  background: #fff;
  color: var(--gw-muted);
  min-width: 108px;
  justify-content: center;
  transition: background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease;
}

.outbox-status.is-sent {
  border-color: rgba(31, 122, 87, 0.25);
  background: rgba(143, 214, 181, 0.2);
  color: var(--gw-forest);
}

.outbox-status.is-queue {
  border-color: rgba(245, 158, 11, 0.3);
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
}

.outbox-status.is-failed {
  border-color: rgba(198, 40, 40, 0.25);
  background: rgba(198, 40, 40, 0.08);
  color: #c62828;
}

.outbox-status.is-idle {
  border-color: var(--gw-line);
  background: var(--gw-fog);
  color: var(--gw-muted);
}

.outbox-status__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #91a59b;
}

.outbox-status.is-sent .outbox-status__dot {
  background: var(--gw-leaf);
  box-shadow: 0 0 0 3px rgba(31, 122, 87, 0.15);
}

.outbox-status.is-queue .outbox-status__dot {
  background: #f59e0b;
}

.outbox-status.is-failed .outbox-status__dot {
  background: #c62828;
}

.outbox-row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.1rem;
}

.outbox-action-edit {
  color: var(--gw-leaf) !important;
}

.outbox-action-delete {
  color: #c62828 !important;
}

.outbox-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  text-align: center;
}

.outbox-empty__title {
  margin-top: 0.75rem;
  font-family: "Outfit", sans-serif;
  font-weight: 700;
  color: var(--gw-ink);
}

.outbox-empty__sub {
  margin-top: 0.25rem;
  font-size: 0.88rem;
  color: var(--gw-muted);
  max-width: 320px;
}

.outbox-mobile {
  padding: 0.85rem;
  display: grid;
  gap: 0.75rem;
}

.outbox-mobile-card {
  border: 1px solid var(--gw-line);
  border-radius: 12px;
  padding: 0.9rem;
  background: #fff;
}

.outbox-mobile-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.outbox-mobile-card__to {
  font-weight: 700;
  color: var(--gw-ink);
  font-family: "Outfit", sans-serif;
}

.outbox-mobile-card__content {
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 10px;
  background: var(--gw-fog);
  color: var(--gw-ink);
  font-size: 0.9rem;
  line-height: 1.45;
}

.outbox-mobile-card__meta {
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.outbox-menu-btn {
  color: var(--gw-forest) !important;
}

.outbox-menu {
  border-radius: 10px !important;
}

.outbox-dialog-card {
  border-radius: 14px !important;
  overflow: hidden;
}

.outbox-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.95rem 1.1rem;
  background:
    radial-gradient(120% 80% at 0% 0%, #1a6b4d 0%, transparent 55%),
    linear-gradient(175deg, #0b3d2e 0%, #0f5c45 45%, #0a3327 100%);
  color: #f4fbf7;
}

.outbox-dialog-header__copy {
  display: flex;
  align-items: center;
  font-family: "Outfit", "Montserrat", sans-serif;
  font-weight: 600;
}

.outbox-dialog-header__copy .v-icon {
  color: var(--gw-mint) !important;
}

.outbox-dialog-body {
  padding: 1.25rem 1.25rem 0.75rem !important;
}

.outbox-field-label {
  margin-bottom: 0.4rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--gw-forest);
}

.outbox-field >>> .v-input__slot {
  border-radius: 10px !important;
}

.outbox-field >>> .v-icon {
  color: var(--gw-leaf) !important;
}

.outbox-schedule-hint {
  margin-top: 0.45rem;
  font-size: 0.78rem;
  color: var(--gw-muted);
}

.outbox-divider {
  border-color: var(--gw-line) !important;
}

.outbox-dialog-actions {
  padding: 0.9rem 1.1rem !important;
  background: var(--gw-paper);
}

.outbox-btn-ghost {
  color: var(--gw-muted) !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}

@media (max-width: 600px) {
  .outbox-toolbar__actions {
    width: 100%;
  }

  .outbox-search {
    max-width: none;
    flex: 1;
    min-width: 0;
  }

  .outbox-toolbar__actions .outbox-btn-primary {
    width: 100%;
  }
}
</style>
